/**
 * The quantity affordance's copy, as data (Chapter 10, F4 + F5).
 *
 * Two things the configurator never said out loud:
 *
 *   - F5 — the quantity input has always accepted any integer, but the ± 25
 *     buttons dominated it, so testers concluded "you can only choose 25, 50 or
 *     100". 87 is a legitimate order.
 *   - F4 — nothing told the customer which price band they were in, or that
 *     ordering a few more boxes would drop the unit price.
 *
 * This module turns a quantity plus a price function into the facts the stepper
 * renders. It is pure and separately tested so the honesty rules below are
 * assertions rather than good intentions:
 *
 *   1. Band labels come from `describeBand`. The STORED key `250+` means
 *      250-499; rendering the raw key would tell a customer that 250 and 2,500
 *      are the same band.
 *   2. A saving is only ever reported when the resolved prices actually differ.
 *      Nothing here knows a percentage; it asks `unitPriceAt` twice and
 *      subtracts. The live Office box carries no band prices at all
 *      (`flow-office.json` — 33 priced options, zero `tieredPrices`), so on
 *      Office the two answers are equal and NO saving is claimed. A flat-priced
 *      box must not grow a discount it does not have.
 *   3. A saving that rounds to 0% is not a saving.
 */

import { TIER_BANDS, describeBand, getActiveTier, type Tier } from './pricing';

export interface NextBandSaving {
  /** The band key — for tests/telemetry. Never render this raw. */
  readonly tier: Tier;
  /** The smallest quantity that reaches the band ("order 100+"). */
  readonly minQty: number;
  /** Whole-percent saving on the per-box price. Always >= 1. */
  readonly percent: number;
}

export interface QuantityBandHint {
  /** The band the quantity falls in; `null` below the lowest band's floor. */
  readonly tier: Tier | null;
  /** Human range for `tier` ("50-99", "250-499", "1000+"); `null` when none. */
  readonly bandLabel: string | null;
  /** The next band up, ONLY when reaching it genuinely costs less per box. */
  readonly nextSaving: NextBandSaving | null;
}

/**
 * @param qty          the quantity the customer has entered
 * @param unitPriceAt  price of one box at a hypothetical quantity — the real
 *                     resolved price (`computeUnitBoxPrice`), never an estimate
 */
export function buildQuantityBandHint(
  qty: number,
  unitPriceAt: (atQty: number) => number,
): QuantityBandHint {
  const tier = getActiveTier(qty);
  const bandLabel = tier === null ? null : describeBand(tier);

  // Below the lowest floor there is no active band, but the FIRST band is still
  // the next one up — "order 25+ to save X%" is the useful thing to say there.
  const currentIndex = tier === null ? -1 : TIER_BANDS.findIndex((b) => b.key === tier);
  const next = TIER_BANDS[currentIndex + 1];

  return { tier, bandLabel, nextSaving: next ? saving(next.key, next.min, qty, unitPriceAt) : null };
}

function saving(
  tier: Tier,
  minQty: number,
  qty: number,
  unitPriceAt: (atQty: number) => number,
): NextBandSaving | null {
  const now = unitPriceAt(qty);
  // Nothing selected / nothing priced — there is no percentage to compute.
  if (!(now > 0)) return null;
  const later = unitPriceAt(minQty);
  if (!(later < now)) return null; // equal (the flat-priced case) or dearer
  const percent = Math.round(((now - later) / now) * 100);
  if (percent < 1) return null; // rounds to nothing — do not claim it
  return { tier, minQty, percent };
}
